import type { SortingState } from '@tanstack/react-table';
import type { TableColumn } from '../components/Table';
import type { FilterGroupItem, FilterItem, FilterLineItem, FilterValueConfig } from './filterSettings';

/**
 * Table state a `ClientTable` applies to rows held in the browser: the same filters, search
 * and sorting a `ServerTable` sends to the API. `ClientTable` pages the result itself.
 */
export type ClientTableState = {
  filters: FilterGroupItem;
  sorting: SortingState;
  globalFilter?: string;
};

/** Column meta read by {@link applyClientTableState}. */
export type ClientTableColumnMeta<TData> = {
  filterValueConfig?: FilterValueConfig;
  /** Sort by this instead of the column value, e.g. a pipeline position for a status. */
  sortKey?: (row: TData) => unknown;
  /**
   * Sort with this comparator instead, for orders a key can't express, e.g. a tiebreak chain. Return the ascending
   * order; descending reverses it. Takes precedence over `sortKey`. Like any sort, the column needs a value
   * (`accessorKey` or `accessorFn`) for its header to offer sorting.
   */
  sortCompare?: (a: TData, b: TData) => number;
};

type ValueKind = 'date' | 'other';

type ColumnReader<TData> = {
  get: (row: TData) => unknown;
  sortBy: (row: TData) => unknown;
  kind: ValueKind;
};

type Readers<TData> = {
  /** Columns with a value, read by filters, search and sorting. */
  values: Map<string, ColumnReader<TData>>;
  /** Columns that sort with their own comparator. */
  comparers: Map<string, (a: TData, b: TData) => number>;
};

const VALUELESS_OPERATORS = new Set(['isNull', 'isEmpty', 'isNotEmpty']);

function readPath(value: unknown, path: string): unknown {
  let current = value;
  for (const key of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function readerFor<TData>(column: TableColumn<TData>): ColumnReader<TData> | undefined {
  let read: ((row: TData) => unknown) | undefined;
  if ('accessorFn' in column && column.accessorFn) {
    const accessorFn = column.accessorFn;
    read = (row) => accessorFn(row, 0);
  } else if ('accessorKey' in column && column.accessorKey) {
    const accessorKey = String(column.accessorKey);
    read = (row) => readPath(row, accessorKey);
  }
  if (!read) return undefined;

  const meta = column.meta as ClientTableColumnMeta<TData> | undefined;
  const valueConfig = meta?.filterValueConfig;
  // A filter accessor points inside an object-valued cell, e.g. `name` for an assignee.
  const accessor = valueConfig && 'accessor' in valueConfig ? valueConfig.accessor : undefined;
  const readColumn = read;
  const get = accessor ? (row: TData) => readPath(readColumn(row), accessor) : readColumn;

  return { get, sortBy: meta?.sortKey ?? get, kind: valueConfig?.type === 'date' ? 'date' : 'other' };
}

function buildReaders<TData>(
  columns: TableColumn<TData>[],
  readers: Readers<TData> = { values: new Map(), comparers: new Map() },
): Readers<TData> {
  for (const column of columns) {
    if ('columns' in column && column.columns) {
      buildReaders(column.columns as TableColumn<TData>[], readers);
      continue;
    }
    const reader = readerFor(column);
    if (reader) readers.values.set(column.id, reader);
    const sortCompare = (column.meta as ClientTableColumnMeta<TData> | undefined)?.sortCompare;
    if (reader && sortCompare) readers.comparers.set(column.id, sortCompare);
  }
  return readers;
}

const isBlank = (value: unknown) => value == null || value === '' || (Array.isArray(value) && value.length === 0);

// Local calendar day as YYYYMMDD, matching the day the date picker selects.
function toLocalDay(value: unknown): number | null {
  if (value == null || value === '') return null;
  // `new Date('2026-10-15')` parses as UTC midnight, which is the previous day west of UTC.
  const dateOnly = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (dateOnly) return Number(dateOnly[1]) * 10000 + Number(dateOnly[2]) * 100 + Number(dateOnly[3]);
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

function compareValues(a: unknown, b: unknown, kind: ValueKind): number | null {
  // `Number(null)` is 0, so blanks would otherwise compare as zero.
  if (isBlank(a) || isBlank(b)) return null;
  if (kind === 'date') {
    const dayA = toLocalDay(a);
    const dayB = toLocalDay(b);
    if (dayA == null || dayB == null) return null;
    return dayA - dayB;
  }
  const numberA = typeof a === 'number' ? a : Number(a);
  const numberB = typeof b === 'number' ? b : Number(b);
  if (!Number.isNaN(numberA) && !Number.isNaN(numberB) && a !== '' && b !== '') {
    return numberA - numberB;
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

function matchesLine<TData>(row: TData, readers: Map<string, ColumnReader<TData>>, item: FilterLineItem): boolean {
  const columnId = item.filterType?.id;
  const reader = columnId ? readers.get(columnId) : undefined;
  if (!reader) return true;
  const operator = item.operatorType?.id ?? 'eq';
  const value = reader.get(row);

  if (VALUELESS_OPERATORS.has(operator)) {
    // Matches the API: `isNull` is only null, `isEmpty` also matches blank text and empty lists.
    if (operator === 'isNull') return value == null;
    return operator === 'isNotEmpty' ? !isBlank(value) : isBlank(value);
  }

  const expected = item.value;
  if (expected == null || expected === '') return true;
  const text = String(value ?? '').toLowerCase();

  switch (operator) {
    case 'eq':
      return compareValues(value, expected, reader.kind) === 0;
    case 'neq':
      return compareValues(value, expected, reader.kind) !== 0;
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const comparison = compareValues(value, expected, reader.kind);
      if (comparison == null) return false;
      if (operator === 'gt') return comparison > 0;
      if (operator === 'gte') return comparison >= 0;
      if (operator === 'lt') return comparison < 0;
      return comparison <= 0;
    }
    case 'like':
      return text.includes(expected.toLowerCase());
    case 'nlike':
      return !text.includes(expected.toLowerCase());
    case 'in':
    case 'nin': {
      const options = expected.split(',').map((option) => option.trim().toLowerCase());
      return operator === 'in' ? options.includes(text) : !options.includes(text);
    }
    default:
      return true;
  }
}

// Lines missing a column, or a value their operator needs, are left out like the API leaves them out.
function isCompleteLine<TData>(readers: Map<string, ColumnReader<TData>>, item: FilterLineItem): boolean {
  const columnId = item.filterType?.id;
  if (!columnId || !readers.has(columnId)) return false;
  const operator = item.operatorType?.id ?? 'eq';
  return VALUELESS_OPERATORS.has(operator) || (item.value != null && item.value !== '');
}

function matchesGroup<TData>(row: TData, readers: Map<string, ColumnReader<TData>>, group: FilterGroupItem): boolean {
  const items = group.items.filter((item) => item.type === 'group' || isCompleteLine(readers, item));
  if (items.length === 0) return true;
  const test = (item: FilterItem) =>
    item.type === 'group' ? matchesGroup(row, readers, item) : matchesLine(row, readers, item);
  return group.operator === 'or' ? items.some(test) : items.every(test);
}

function matchesSearch<TData>(row: TData, readers: Map<string, ColumnReader<TData>>, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  for (const reader of readers.values()) {
    const value = reader.get(row);
    // Object cells without a filter accessor would otherwise match on "[object Object]".
    if (value == null || (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date))) continue;
    if (String(value).toLowerCase().includes(needle)) return true;
  }
  return false;
}

function sortRows<TData>(rows: TData[], readers: Readers<TData>, sorting: SortingState): TData[] {
  const active = sorting.flatMap((sort) => {
    const compare = readers.comparers.get(sort.id);
    const reader = readers.values.get(sort.id);
    return reader ? [{ compare, reader, desc: sort.desc }] : [];
  });
  if (active.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const { compare, reader, desc } of active) {
      if (compare) {
        const comparison = compare(a, b);
        if (comparison !== 0) return desc ? -comparison : comparison;
        continue;
      }
      const valueA = reader.sortBy(a);
      const valueB = reader.sortBy(b);
      const blankA = isBlank(valueA);
      const blankB = isBlank(valueB);
      // Blanks sink to the bottom in both directions.
      if (blankA && blankB) continue;
      if (blankA) return 1;
      if (blankB) return -1;
      const comparison = compareValues(valueA, valueB, reader.kind) ?? 0;
      if (comparison !== 0) return desc ? -comparison : comparison;
    }
    return 0;
  });
}

function matchesState<TData>(row: TData, readers: Readers<TData>, state: ClientTableState): boolean {
  return (
    matchesGroup(row, readers.values, state.filters) && matchesSearch(row, readers.values, state.globalFilter ?? '')
  );
}

/**
 * Applies table state (advanced filters, search and sorting) to rows held in memory and returns
 * every matching row in order. Supports every operator the filter dialog offers, AND/OR groups at
 * any depth, nested `filterValueConfig.accessor` paths and date columns compared by local calendar day.
 */
export function applyClientTableState<TData>(
  rows: TData[],
  columns: TableColumn<TData>[],
  state: ClientTableState,
): TData[] {
  const readers = buildReaders(columns);
  return sortRows(
    rows.filter((row) => matchesState(row, readers, state)),
    readers,
    state.sorting,
  );
}
